// apps/web/src/components/shell/AppTopbar.jsx
// Globale Plattformzeile (kompakt) + prominente App-Identität darunter bzw. mobil in einer Grid-Zeile.
// AppLauncher über Logo-Button. Sichtbare Texte via i18n.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  ChevronDown,
  Building2,
  UserCircle2,
  LogOut,
  PlusCircle,
  CreditCard,
  FlaskConical,
} from "lucide-react";
import {
  AppLauncherSheet,
  AppLauncherContent,
  isAppVisibleInLauncher,
} from "../platform/index.js";
import { PlatformSideNavMobileToggle } from "../platform/PlatformSideNav.jsx";
import { AuthAvatarFace } from "./AuthAvatarFace.jsx";
import { buildAppHref } from "../../utils/href.js";
import { normalizeGovernanceAppKey } from "@tennis-matches/services/governance";

function read(t, key, fallback = "") {
  const v = typeof t === "function" ? t(key) : key;
  return (v && v !== key ? v : fallback || key || "").toString();
}

/** Login-/OAuth-Rückweg: kein redundanter „Anmelden“-Link im Header. */
function shouldHideHeaderSignInLink(pathname) {
  const raw = String(pathname || "").trim();
  const norm = raw.replace(/\/+$/, "") || "/";
  if (norm === "/login") return true;
  if (norm === "/auth/login") return true;
  if (norm === "/auth") return true;
  if (norm.startsWith("/auth/")) return true;
  return false;
}

function __asStr(v) {
  return String(v ?? "").trim();
}

function TennisBallIcon({ size = 18 }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M4.2 7.2 C 7.6 10.2, 7.6 13.8, 4.2 16.8" />
      <path d="M19.8 7.2 C 16.4 10.2, 16.4 13.8, 19.8 16.8" />
    </svg>
  );
}

export function AppTopbar({
  t,
  shellAppTitleKey = "",
  shellAppSubtitleKey = "",
  /** Angemeldet — steuert Menü vs. Login unabhängig von erkanntem Label (Trainer ohne E-Mail in user.email). */
  authenticated = false,
  authUserLabel = "",
  authUserEmail = "",
  authUserAvatar = "",
  authUserInitials = "",
  /** @type {{ accessReady?: boolean, isSuperAdmin?: boolean, allowedAppKeys?: Set<string>|null }} */
  launcherAccess = null,
  onSignOut,
  // eslint-disable-next-line no-unused-vars
  onSignIn,
  canAccessAccessRequestsAdmin = false,
  /** TrainingsDesk-Vereinsabo (Subscription) im Avatar-Menü. */
  canAccessVereinsabo = false,
  // eslint-disable-next-line no-unused-vars
  enablePlatformAppSwitcher = false,
  /** CoachDesk: große Desktop-Ansicht – Chevron neben Avatar (Sichtbarkeit per CSS ≥1025px). */
  coachDeskDesktopUserChrome = false,
  /** Ausstehende Zugriffsanfrage ohne App-Rechte: nur Abmelden im Nutzermenü. */
  shellAccessRestricted = false,
  /** Persönliche Startseite (Route-Name, z. B. clubhub, trainerstunden, hub). */
  homeRoute = "hub",
  onGoHome,
  /** TrainingsDesk: Link zu eigenen Trainer-Stammdaten (nur reiner Trainer-Login). */
  coachDeskSelfProfileHref = "",
  /** Aktuell auf der globalen Startseite (z. B. /hub)? Dann öffnet der Logo-Klick KEINEN
      AppLauncher (User ist bereits zu Hause), sondern scrollt nur sanft nach oben.
      Damit hat der Header auf Hub und allen anderen Seiten denselben Look – nur das
      Logo-Verhalten unterscheidet sich. */
  homeMode = false,
  /** Liste der Vereine, in denen der Nutzer Mitglied ist – wird im Avatar-Menü
      als Read-only-Sektion angezeigt. Aus deriveMyClubsFromBindings(). */
  userClubs = null,
  /** Globale Seitennavigation aktiv — ersetzt App-Launcher-Kacheln. */
  sideNavEnabled = false,
  onOpenSideNav = null,
  sideNavMobileOpen = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const hideSignInLink = shouldHideHeaderSignInLink(location.pathname);
  const [launcherOpen, setLauncherOpen] = useState(false);
  const launcherBtnRef = useRef(null);
  const userMenuDetailsRef = useRef(null);

  const closeUserMenu = () => {
    const el = userMenuDetailsRef.current;
    if (el && typeof el.removeAttribute === "function") {
      el.removeAttribute("open");
    }
  };
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.matchMedia?.("(max-width: 640px)")?.matches ?? false;
    } catch {
      return false;
    }
  });
  const [isMobileShell, setIsMobileShell] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.matchMedia?.("(max-width: 767px)")?.matches ?? false;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let mq = null;
    let mqShell = null;
    try {
      mq = window.matchMedia?.("(max-width: 640px)") || null;
      mqShell = window.matchMedia?.("(max-width: 767px)") || null;
    } catch {
      mq = null;
      mqShell = null;
    }
    if (!mq && !mqShell) return undefined;

    const onChange = () => {
      if (mq) setIsMobile(!!mq.matches);
      if (mqShell) setIsMobileShell(!!mqShell.matches);
    };
    onChange();

    const cleanups = [];
    for (const media of [mq, mqShell]) {
      if (!media) continue;
      try {
        media.addEventListener("change", onChange);
        cleanups.push(() => media.removeEventListener("change", onChange));
      } catch {
        media.addListener?.(onChange);
        cleanups.push(() => media.removeListener?.(onChange));
      }
    }
    return () => cleanups.forEach((fn) => fn());
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const onPointerDown = (e) => {
      const el = userMenuDetailsRef.current;
      if (!el || !el.hasAttribute("open")) return;
      if (el.contains(e.target)) return;
      el.removeAttribute("open");
    };
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      const el = userMenuDetailsRef.current;
      if (el && el.hasAttribute("open")) el.removeAttribute("open");
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    closeUserMenu();
  }, [location.pathname]);

  const platformMarkFull = read(t, "appTopbar.platformMark");
  const platformMarkShort = read(t, "appTopbar.platformMarkShort");
  const launcherHint = read(t, "appTopbar.launcherHint");
  const launcherRestrictedHint = read(t, "appTopbar.launcherRestrictedPending");
  const signInLabel = read(t, "auth.signIn");
  const signedInAs = read(t, "auth.signedInAs");
  const menuLabel = read(t, "nav.menu");
  const myDataLabel = read(t, "appTopbar.menu.myData");
  const myProfileLabel = read(t, "hub.userMenu.profile");
  const myClubsLabel = read(t, "hub.userMenu.myClubs");
  const signOutLabel = read(t, "auth.signOut");
  const menuAccountEmail = __asStr(authUserEmail);
  const menuAccountName = __asStr(authUserLabel);
  const showMyDataMenuLabel = Boolean(__asStr(coachDeskSelfProfileHref));
  const profileMenuLabel = showMyDataMenuLabel ? myDataLabel : myProfileLabel;
  const safeUserClubs = Array.isArray(userClubs)
    ? userClubs.filter((c) => c && c.id)
    : [];
  const connectClubLabel = read(
    t,
    safeUserClubs.length > 0
      ? "hub.userMenu.connectAnotherClub"
      : "hub.userMenu.connectClub",
  );

  const hasAppIdentity = !!String(shellAppTitleKey || "").trim();
  const appTitle = hasAppIdentity ? read(t, shellAppTitleKey) : "";
  const sk = String(shellAppSubtitleKey || "").trim();
  const appSubtitle = hasAppIdentity && sk ? read(t, sk) : "";

  const closeMenuDetailsFromEvent = (event) => {
    const detailsEl = event?.currentTarget?.closest?.("details");
    if (detailsEl && typeof detailsEl.removeAttribute === "function") {
      detailsEl.removeAttribute("open");
    }
  };

  const handleConnectClub = (event) => {
    closeMenuDetailsFromEvent(event);
    if (homeMode && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("tm:hub-connect-club"));
      return;
    }
    navigate("/profile/kontaktdaten");
  };

  const renderUserMenuPanel = () => (
    <div className="app-topbar__menuPanel" role="menu" aria-label={menuLabel}>
      <div className="app-topbar__menuSection app-topbar__menuSection--account">
        <div className="app-topbar__menuHead app-topbar__menuHead--profile">
          <div className="app-topbar__menuHeadText">
            {menuAccountName ? (
              <strong className="app-topbar__menuPersonName">
                {menuAccountName}
              </strong>
            ) : null}
            {menuAccountEmail ? (
              <span className="muted app-topbar__menuPersonEmail">
                {menuAccountEmail}
              </span>
            ) : null}
            {!menuAccountName && !menuAccountEmail ? (
              <div className="muted app-topbar__menuMeta">
                {signedInAs}: {authUserLabel}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {!shellAccessRestricted ? (
        <div className="app-topbar__menuSection app-topbar__menuSection--primary">
          <button
            type="button"
            className="btn btn--ghost btn--sm app-topbar__menuAction"
            onClick={(e) => {
              closeMenuDetailsFromEvent(e);
              navigate("/profile/kontaktdaten");
            }}
          >
            <UserCircle2 size={16} strokeWidth={2} aria-hidden="true" />
            <span>{profileMenuLabel}</span>
          </button>
        </div>
      ) : null}
      {!shellAccessRestricted && safeUserClubs.length > 0 ? (
        <div className="app-topbar__menuSection app-topbar__menuSection--clubs">
          <div className="app-topbar__menuSectionTitle">{myClubsLabel}</div>
          <ul className="app-topbar__menuClubsList">
            {safeUserClubs.map((club) => (
              <li
                key={club.id}
                className="app-topbar__menuClub"
                title={club.label}
              >
                <Building2 size={14} strokeWidth={2} aria-hidden="true" />
                <span>{club.label}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {!shellAccessRestricted ? (
        <div className="app-topbar__menuSection">
          <button
            type="button"
            className="btn btn--ghost btn--sm app-topbar__menuAction"
            onClick={handleConnectClub}
          >
            <PlusCircle size={16} strokeWidth={2} aria-hidden="true" />
            <span>{connectClubLabel}</span>
          </button>
        </div>
      ) : null}
      {!shellAccessRestricted && canAccessVereinsabo ? (
        <div className="app-topbar__menuSection">
          <button
            type="button"
            className="btn btn--ghost btn--sm app-topbar__menuAction"
            onClick={(e) => {
              closeMenuDetailsFromEvent(e);
              navigate("/trainingsdesk/stammdaten/vereinsabo");
            }}
          >
            <CreditCard size={16} strokeWidth={2} aria-hidden="true" />
            <span>{read(t, "hub.userMenu.vereinsabo")}</span>
          </button>
        </div>
      ) : null}
      {!shellAccessRestricted && canAccessAccessRequestsAdmin ? (
        <div className="app-topbar__menuSection">
          <button
            type="button"
            className="btn btn--ghost btn--sm app-topbar__menuAction"
            onClick={(e) => {
              closeMenuDetailsFromEvent(e);
              navigate("/access-requests-admin");
            }}
          >
            {read(t, "hub.accessRequests.title")}
          </button>
        </div>
      ) : null}
      {!shellAccessRestricted && launcherAccess?.isSuperAdmin ? (
        <div className="app-topbar__menuSection">
          <button
            type="button"
            className="btn btn--ghost btn--sm app-topbar__menuAction"
            onClick={(e) => {
              closeMenuDetailsFromEvent(e);
              navigate("/superadmin-clubs");
            }}
          >
            {read(t, "superadmin.tiles.clubs.title")}
          </button>
        </div>
      ) : null}
      {import.meta.env.DEV &&
      !shellAccessRestricted &&
      launcherAccess?.isSuperAdmin ? (
        <div className="app-topbar__menuSection">
          <button
            type="button"
            className="btn btn--ghost btn--sm app-topbar__menuAction"
            onClick={(e) => {
              closeMenuDetailsFromEvent(e);
              navigate("/dev/qa");
            }}
          >
            <FlaskConical size={16} strokeWidth={2} aria-hidden="true" />
            <span>{read(t, "dev.qa.menuLabel")}</span>
          </button>
        </div>
      ) : null}

      {isMobileShell ? (
        <div className="app-topbar__menuSection app-topbar__menuSection--legal">
          <a
            className="app-topbar__menuLegalLink"
            href="/impressum"
            onClick={closeMenuDetailsFromEvent}
          >
            {read(t, "footer.impressum", "Impressum")}
          </a>
          <a
            className="app-topbar__menuLegalLink"
            href="/datenschutz"
            onClick={closeMenuDetailsFromEvent}
          >
            {read(t, "footer.privacy", "Datenschutz")}
          </a>
        </div>
      ) : null}

      <div className="app-topbar__menuSection app-topbar__menuSection--logout">
        <button
          type="button"
          className="btn btn--ghost btn--sm app-topbar__menuAction app-topbar__menuAction--danger"
          onClick={(e) => {
            closeMenuDetailsFromEvent(e);
            if (typeof onSignOut === "function") onSignOut();
          }}
        >
          <LogOut size={16} strokeWidth={2} aria-hidden="true" />
          <span>{signOutLabel}</span>
        </button>
      </div>
    </div>
  );

  const renderAuthSlot = () => (
    <div className="app-topbar__auth" data-auth>
      {authenticated ? (
        <details
          ref={userMenuDetailsRef}
          className="app-topbar__menu"
          data-menu
        >
          <summary className="app-topbar__menuTrigger" aria-label={menuLabel}>
            <span
              className={`app-topbar__menuTriggerFace${coachDeskDesktopUserChrome ? " app-topbar__menuTriggerFace--coachDesk" : ""}`.trim()}
            >
              <AuthAvatarFace
                avatarUrl={authUserAvatar}
                initials={authUserInitials}
              />
              {coachDeskDesktopUserChrome ? (
                <ChevronDown
                  className="app-topbar__menuChevron"
                  size={16}
                  strokeWidth={2}
                  aria-hidden
                />
              ) : null}
            </span>
          </summary>
          {renderUserMenuPanel()}
        </details>
      ) : hideSignInLink ? (
        <span className="app-topbar__authVacant" aria-hidden="true" />
      ) : (
        <Link
          to="/login"
          className="hub-header__signin"
          aria-label={signInLabel}
        >
          {signInLabel}
        </Link>
      )}
    </div>
  );

  return (
    <header
      className={`hub-header hub-header--sticky app-topbar${hasAppIdentity ? " app-topbar--withAppIdentity" : ""}`.trim()}
      role="banner"
    >
      <div className="hub-header__inner app-topbar__inner">
        <div
          className={`app-topbar__shell${hasAppIdentity ? " app-topbar__shell--withIdentity" : ""}`.trim()}
        >
          {sideNavEnabled ? (
            <PlatformSideNavMobileToggle
              t={t}
              open={sideNavMobileOpen}
              onClick={() => {
                if (shellAccessRestricted) return;
                closeUserMenu();
                if (typeof onOpenSideNav === "function") onOpenSideNav();
              }}
            />
          ) : null}
          <button
            type="button"
            ref={launcherBtnRef}
            className={`hub-header__logo hub-header__logo--brand app-topbar__logoButton app-topbar__launcherBtn${shellAccessRestricted ? " app-topbar__launcherBtn--restricted" : ""}${homeMode || sideNavEnabled ? " app-topbar__launcherBtn--home" : ""}${sideNavEnabled ? " app-topbar__logoButton--sideNav" : ""}`.trim()}
            aria-label={
              shellAccessRestricted
                ? launcherRestrictedHint
                : read(t, "nav.home")
            }
            title={
              shellAccessRestricted
                ? launcherRestrictedHint
                : read(t, "nav.home")
            }
            aria-disabled={shellAccessRestricted ? true : undefined}
            disabled={shellAccessRestricted}
            onClick={() => {
              if (shellAccessRestricted) return;
              closeUserMenu();
              if (sideNavEnabled || homeMode) {
                if (typeof onGoHome === "function") {
                  onGoHome();
                  return;
                }
                if (homeMode) {
                  if (
                    typeof window !== "undefined" &&
                    typeof window.scrollTo === "function"
                  ) {
                    try {
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    } catch {
                      window.scrollTo(0, 0);
                    }
                  }
                  return;
                }
                navigate("/hub");
                return;
              }
              setLauncherOpen(true);
            }}
          >
            <span
              className="hub-header__logo-badge hub-header__logo-badge--ball app-topbar__logoBadge"
              aria-hidden
            >
              <TennisBallIcon size={18} />
            </span>
            {hasAppIdentity ? (
              <>
                <span
                  className="hub-header__brand-divider app-topbar__brandDividerDesk"
                  aria-hidden
                />
                <span className="hub-header__logo-text app-topbar__platformMark app-topbar__platformMark--desktop">
                  {platformMarkFull}
                </span>
                <span
                  className="hub-header__logo-text app-topbar__platformMark app-topbar__platformMark--mobileShort"
                  aria-hidden
                >
                  {platformMarkShort}
                </span>
              </>
            ) : (
              <span
                className="hub-header__logo-text app-topbar__platformMark app-topbar__platformMark--collapsed"
                aria-hidden
              >
                {platformMarkShort}
              </span>
            )}
            <span className="sr-only">{platformMarkFull}</span>
          </button>

          {hasAppIdentity ? (
            homeMode ? (
              <span className="app-topbar__appTitleSpacer" aria-hidden />
            ) : (
              <h1 className="app-topbar__appTitle">{appTitle}</h1>
            )
          ) : (
            <span className="app-topbar__appTitleSpacer" aria-hidden />
          )}

          <div className="app-topbar__right app-topbar__authSlot">
            {isMobile ? (
              <div className="app-topbar__mobile" data-mobile-topbar>
                {renderAuthSlot()}
              </div>
            ) : (
              renderAuthSlot()
            )}
          </div>

          {appSubtitle ? (
            <p className="app-topbar__appSubtitle muted">{appSubtitle}</p>
          ) : null}
        </div>
      </div>
      {!sideNavEnabled ? (
        <AppLauncherSheet
          open={!shellAccessRestricted && launcherOpen}
          anchorRef={launcherBtnRef}
          onClose={() => setLauncherOpen(false)}
        >
          <AppLauncherContent
            t={t}
            launcherAccess={launcherAccess}
            onSelectApp={(app) => {
              const key = normalizeGovernanceAppKey(
                String(app?.appKey || app?.id || "").trim(),
              );
              if (!key) return;
              const la = launcherAccess;
              if (la == null || la.accessReady === false) return;
              if (
                !isAppVisibleInLauncher(key, {
                  allowedAppKeys: la.allowedAppKeys,
                })
              ) {
                return;
              }
              const href = buildAppHref(key);
              setLauncherOpen(false);
              queueMicrotask(() => navigate(href, { replace: false }));
            }}
            showDiscoverApps={false}
            homeMode={homeMode}
            onGoHome={() => {
              setLauncherOpen(false);
              queueMicrotask(() => {
                if (typeof onGoHome === "function") {
                  onGoHome();
                  return;
                }
                const routeName = String(homeRoute || "hub").trim() || "hub";
                navigate(routeName === "hub" ? "/hub" : `/${routeName}`, {
                  replace: false,
                });
              });
            }}
          />
        </AppLauncherSheet>
      ) : null}
    </header>
  );
}
